import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import translationsData from '../../data/translations.json';
import { handleLocalApiCall } from '../../api/localGameBackend';

const GameContext = createContext();

const MISSION_URL_PREFIX = process.env.REACT_APP_MISSION_URL;

export const GameProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [missionCode, setMissionCode] = useState(null);
  const [currentCounter, setCurrentCounter] = useState(null);
  const [currentCards, setCurrentCards] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(() => localStorage.getItem('language') || 'EN');
  const [cardLang, setCardLang] = useState(() => localStorage.getItem('cardLanguage') || 'EN');
  const [translations, setTranslations] = useState(translationsData);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [gameStatus, setGameStatus] = useState('initial');
  const [lastScannedCardCode, setLastScannedCardCode] = useState(null);
  const [availableSolutionCards, setAvailableSolutionCards] = useState({});
  const [selectedSolutionCards, setSelectedSolutionCards] = useState({});
  const [zoomStep, setZoomStep] = useState(0);
  const [uiTheme, setUiTheme] = useState('countdown');

  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const toastTimeoutRef = useRef(null);
  const fadeOutTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  const alertModalRef = useRef(null);
  const [alertModalState, setAlertModalState] = useState({
    title: '',
    message: '',
    isConfirmation: false,
    onConfirm: null,
    onClose: null,
    confirmButtonText: '',
    cancelButtonText: ''
  });

  const hideToastMessage = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    if (fadeOutTimeoutRef.current) {
      clearTimeout(fadeOutTimeoutRef.current);
    }
    setShowToast(false);
    setToastMessage('');
  }, []);

  const showToastMessage = useCallback((message) => {
    hideToastMessage();

    setToastMessage(message);
    setShowToast(true);

    toastTimeoutRef.current = setTimeout(() => {
      setShowToast(false);
      fadeOutTimeoutRef.current = setTimeout(() => {
        setToastMessage('');
      }, 1000);
    }, 5000);
  }, [hideToastMessage]);

  const makeApiCall = useCallback(async (method, path, body = undefined, suppressLoadingIndicator = false) => {
    if (!suppressLoadingIndicator) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await handleLocalApiCall(method, path, body);
      return data;
    } catch (err) {
      console.error(`Request error ${method} ${path}:`, err);
      const errorMessage = err.message || translations[lang]?.unknown_error_occurred;
      setError(errorMessage);
      throw err;
    } finally {
      if (!suppressLoadingIndicator) {
        setIsLoading(false);
      }
    }
  }, [setError, lang, translations]);

  const showAlertModal = useCallback((title, message, isConfirmation, onConfirm, onClose, confirmButtonText, cancelButtonText) => {
    const wrappedOnConfirm = () => {
      if (alertModalRef.current) {
        const bsModalInstance = window.bootstrap.Modal.getInstance(alertModalRef.current);
        if (bsModalInstance) {
          bsModalInstance.hide();
        }
      }
      if (onConfirm) {
        onConfirm();
      }
    };

    const wrappedOnClose = () => {
      if (alertModalRef.current) {
        const bsModalInstance = window.bootstrap.Modal.getInstance(alertModalRef.current);
        if (bsModalInstance) {
          bsModalInstance.hide();
        }
      }
      if (onClose) {
        onClose();
      }
    };

    setAlertModalState({
      title,
      message,
      isConfirmation,
      onConfirm: wrappedOnConfirm,
      onClose: wrappedOnClose,
      confirmButtonText,
      cancelButtonText
    });
    alertModalRef.current?.show();
  }, [alertModalRef]);

  const validateCodeAndFetchLanguages = useCallback(async (code) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await makeApiCall('GET', `/game/validate/${code}`);
      const fetchedLanguages = response.Languages || [];
      setAvailableLanguages(fetchedLanguages);
      setMissionCode(code);
      setUiTheme(response.Ui || 'countdown');

      if (fetchedLanguages.length === 0) {
        setError(translations[lang]?.error_code_not_found);
        setUiTheme('countdown');
        return false;
      }
      
      let effectiveCardLang = 'EN';
      if (fetchedLanguages.includes(lang)) {
        effectiveCardLang = lang;
      } else if (fetchedLanguages.includes('EN')) {
        effectiveCardLang = 'EN';
      } else {
        effectiveCardLang = fetchedLanguages[0];
      }
      setCardLang(effectiveCardLang);
      localStorage.setItem('cardLanguage', effectiveCardLang);
      return true;
    } catch (err) {
      setError(translations[lang]?.error_code_not_found);
      setAvailableLanguages([]);
      setMissionCode(null);
      setUiTheme('countdown');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [makeApiCall, lang, translations]);

  const createSession = useCallback(async () => {
    if (!missionCode) {
      setError(translations[lang]?.error_code_not_found);
      return;
    }
    try {
        const response = await makeApiCall('POST', '/game/session/create', { code: missionCode, language: cardLang });

        setSessionId(response.SessionId);
        setMissionCode(response.Code);
        localStorage.setItem('sessionId', response.SessionId);
        setCurrentCards(response.Cards);
        setCurrentCounter(response.Counter);
    } catch (err) {
        setError(err.message);
        setSessionId(null);
        setMissionCode(null);
        localStorage.removeItem('sessionId');
    }
  }, [missionCode, cardLang, makeApiCall, lang, translations]);

  const fetchSessionStatus = useCallback(async (sId, suppressLoadingIndicator = false, urlMissionCode = null) => {
    let currentSessionId = sId || sessionId || localStorage.getItem('sessionId');
    let currentCardLang = cardLang || localStorage.getItem('cardLanguage');

    if (!currentSessionId) {
      setError(translations[lang]?.error_no_active_session);
      return null;
    }
    if (!currentCardLang) {
      currentCardLang = 'EN';
      console.warn('Card language not found, defaulting to EN for session status fetch.');
    }

    try {
      const response = await makeApiCall('POST', '/game/session/read', {
        sessionId: currentSessionId,
        language: currentCardLang
      }, suppressLoadingIndicator);

      if (urlMissionCode && response.Code !== urlMissionCode) {
        console.log("URL mission code differs from existing session code. Clearing session data.");
        localStorage.removeItem('sessionId');
        localStorage.removeItem('cardLanguage');
        setSessionId(null);
        setCurrentCards([]);
        setCurrentCounter(null);
        setError(translations[lang]?.error_session_expired_or_not_found);
        return null;
      }

      setSessionId(response.SessionId);
      setCurrentCards(response.Cards);
      setCurrentCounter(response.Counter);
      localStorage.setItem('sessionId', response.SessionId);

      return response;
    } catch (err) {
      console.error('Error fetching session status:', err);
      setError(err.message || translations[lang]?.unknown_error_occurred);

      setSessionId(null);
      setCurrentCards([]);
      setCurrentCounter(null);
      localStorage.removeItem('sessionId');
      localStorage.removeItem('cardLanguage');
      throw err;
    }
  }, [sessionId, cardLang, makeApiCall, setCurrentCards, setCurrentCounter, setError, translations, lang, setSessionId]);

  const pollSessionStatus = useCallback(async () => {
    if (!sessionId) {
      console.log('Polling stopped: No session ID.');
      return;
    }
    try {
      const response = await makeApiCall('POST', '/game/session/read', {
        sessionId: sessionId,
        language: cardLang
      }, true);

      setCurrentCounter(response.Counter);

      if (response.Status === 'gameover' || response.Status === 'victory') {
        console.log(`Game ended with status: ${response.Status}. Redirecting to outcome screen.`);
        setGameStatus(response.Status);
        window.location.hash = 'outcome';
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error during session polling:', err);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [sessionId, cardLang, makeApiCall, setCurrentCounter, setGameStatus]);


  const fetchAvailableCardsForSolution = useCallback(async () => {
    if (!sessionId || !cardLang) {
      setError(translations[lang]?.missing_play_card_params_error);
      return {};
    }
    try {
      const response = await makeApiCall('POST', '/game/card/read', {
        sessionId: sessionId,
        language: cardLang
      });
      setAvailableSolutionCards(response.Cards || {});
    }
    catch (err) {
      console.error('Error fetching available cards for solution:', err);
      setError(err.message || translations[lang]?.error_api_generic);
      setAvailableSolutionCards({});
    }
  }, [sessionId, cardLang, makeApiCall, setError, translations, lang]);

  const submitSolution = useCallback(async (submittedCardIds) => {
    if (!sessionId) {
      showAlertModal(
        translations[lang]?.error_title,
        translations[lang]?.error_no_active_session,
        false, null, null
      );
      return;
    }

    try {
      setIsLoading(true);
      const data = await makeApiCall('POST', `/game/guess`, {
        sessionId: sessionId,
        language: cardLang,
        cards: submittedCardIds
      });

      setCurrentCounter(data.Counter);
      setCurrentCards(data.Cards);

      if (data.Status === 'victory' || data.Status === 'gameover') {
        console.log(`Game ended with status: ${data.Status}. Preparing outcome data and navigating.`);
        setGameStatus(data.Status);
        window.location.hash = 'outcome';
      } else {
        const lastCard = data.Cards && data.Cards.length > 0 ? data.Cards[data.Cards.length - 1] : null;
        if (lastCard && lastCard.Type === 'story' && lastCard.Category === 'tryagain') {
          showAlertModal(
            lastCard.Title || translations[lang]?.incorrect_solution_title,
            lastCard.FieldValue || translations[lang]?.try_again_message,
            false,
            null,
            null
          );
        } else {
          showAlertModal(
            translations[lang]?.incorrect_solution_title,
            translations[lang]?.try_again_message,
            false,
            null,
            null
          );
        }
      }
    } catch (err) {
      console.error('Error verifying solution:', err);
      showAlertModal(
        translations[lang]?.error_title,
        err.message || translations[lang]?.unknown_error_occurred,
        false, null, null
      );
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, cardLang, translations, makeApiCall, showAlertModal, setIsLoading, setCurrentCounter, setCurrentCards, lang, setGameStatus]);


  useEffect(() => {
    if (sessionId && cardLang) {
      fetchAvailableCardsForSolution();
      fetchSessionStatus(sessionId);
    }
  }, [sessionId, cardLang, fetchAvailableCardsForSolution, fetchSessionStatus]);

  useEffect(() => {
    const missionCard = currentCards.find(card => card.Type && card.Type.toLowerCase() === "mission");
    const hasHurryUp = missionCard && missionCard.Modifiers && missionCard.Modifiers.includes("hurry_up");

    if (hasHurryUp && sessionId) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(pollSessionStatus, 10 * 1000);
      console.log('HurryUp polling started.');
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        console.log('HurryUp polling stopped.');
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        console.log('HurryUp polling cleanup.');
      }
    };
  }, [sessionId, currentCards, pollSessionStatus]);


  const returnToInitialScreen = useCallback(() => {
    setGameStatus('initial');
    window.location.hash = 'initial';
  }, [setGameStatus]);

  const navigateToMissionScreen = useCallback(() => {
    setGameStatus('mission');
    window.location.hash = 'mission';
  }, [setGameStatus]);

  const navigateToScanScreen = useCallback(() => {
    setGameStatus('scan');
    window.location.hash = 'scan';
  }, [setGameStatus]);

  const navigateToLastScannedCardScreen = useCallback(() => {
    setGameStatus('lastScanned');
    window.location.hash = 'lastScanned';
  }, [setGameStatus]);

  const navigateToSendScreen = useCallback(() => {
    setGameStatus('send');
    window.location.hash = 'send';
  }, [setGameStatus]);

  const getCardImagePath = useCallback((card) => {
    if (!card) {
        console.warn('getCardImagePath called with null or undefined card.');
        return '';
    }

    if (card.Image && card.Image.trim() !== '') {
        return `/img/${card.Image}`;
    }

    if (card.Category) {
        let imageName = card.Category.toLowerCase();
        if (card.SubCategory && card.SubCategory.trim() !== '') {
            imageName = `${imageName}-${card.SubCategory.toLowerCase()}`;
        }
        return `/assets/cards/${imageName}.png`;
    }
    
    return '';
  }, []);

  const formatFieldValue = useCallback((fieldValue) => {
    if (!fieldValue) return '';
    const regex = /\B\[\$([LPE])(\d+)\]\B/g;
    return fieldValue.replace(regex, (match, type, numStr) => {
      const num = parseInt(numStr, 10);
      let badgeClass = '';
      switch (type) {
          case 'L': badgeClass = 'bg-success'; break;
          case 'P': badgeClass = 'bg-danger'; break;
          case 'E': badgeClass = 'bg-info'; break;
          default: return match;
      }
      return `<span class="badge ${badgeClass}">${num}</span>`;
    });
  }, []);

  const handleQrCodeScanned = useCallback(async (scannedData) => {
    console.log('QR Code scanned (Raw value):', scannedData);

    if (!sessionId) {
      setError(translations[lang]?.error_no_active_session);
      returnToInitialScreen();
      return;
    }

    const missionUrlPrefix = 'https://evapp.pixelings.com/?c=';
    const appSchemaPrefix = 'evapp://';

    if (scannedData.startsWith(missionUrlPrefix)) {
      try {
        const url = new URL(scannedData);
        const codeFromUrl = url.searchParams.get('c');

        if (codeFromUrl) {
          if (MISSION_URL_PREFIX) {
            const newUrl = `${MISSION_URL_PREFIX}${codeFromUrl}`;
            window.location.href = newUrl;
          } else {
            window.location.href = scannedData;
          }
        } else {
          setError(translations[lang]?.invalid_unrecognized_qr_code_error);
        }
      } catch (e) {
        console.error('Error parsing mission card URL:', e);
        setError(translations[lang]?.invalid_unrecognized_qr_code_error);
      }
    } else if (scannedData.startsWith(appSchemaPrefix)) {
      const cardIdToPlay = scannedData.substring(appSchemaPrefix.length);
      const cardExistsInCarousel = currentCards.some(card => card.CardId === cardIdToPlay);

      if (cardExistsInCarousel) {
        setLastScannedCardCode(cardIdToPlay);
        navigateToLastScannedCardScreen();
      } else {
        try {
          const response = await makeApiCall('POST', '/game/card/play', {
            sessionId: sessionId,
            cardId: cardIdToPlay,
            language: cardLang
          });
          setCurrentCards(response.Cards);
          setCurrentCounter(response.Counter);
          setLastScannedCardCode(cardIdToPlay);

          if (response.Status === 'gameover' || response.Status === 'victory') {
            console.log(`Game ended with status: ${response.Status}. Navigating to outcome screen.`);
            setGameStatus(response.Status);
            window.location.hash = 'outcome';
          } else if (response.Status === 'started') {
            console.log(`Card played. Game status: ${response.Status}. Navigating to last scanned card screen.`);
            navigateToLastScannedCardScreen();
          } else {
            console.warn('Unexpected status after playing card:', response.Status);
            navigateToLastScannedCardScreen();
          }

        } catch (err) {
          console.error('Error playing card:', err);
          setError(err.message || translations[lang]?.card_load_error);
        }
      }
    } else {
      setError(translations[lang]?.invalid_unrecognized_qr_code_error);
    }
  }, [sessionId, missionCode, currentCards, cardLang, makeApiCall, setCurrentCards, setCurrentCounter, setLastScannedCardCode, showToastMessage, navigateToMissionScreen, navigateToLastScannedCardScreen, setError, translations, lang, returnToInitialScreen, setGameStatus]);


  const contextValue = {
    sessionId, setSessionId,
    missionCode, setMissionCode,
    currentCounter, setCurrentCounter,
    currentCards, setCurrentCards,
    isLoading, error, setError,
    lang, setLang,
    cardLang, setCardLang,
    translations,
    createSession,
    validateCodeAndFetchLanguages,
    availableLanguages,
    gameStatus, setGameStatus,
    returnToInitialScreen,
    navigateToMissionScreen,
    navigateToScanScreen,
    navigateToLastScannedCardScreen,
    navigateToSendScreen,
    lastScannedCardCode, setLastScannedCardCode,
    alertModalRef,
    alertModalState,
    showAlertModal,
    handleQrCodeScanned,
    fetchSessionStatus,
    fetchAvailableCardsForSolution,
    getCardImagePath,
    formatFieldValue,
    showToast,
    toastMessage,
    showToastMessage,
    hideToastMessage,
    availableSolutionCards,
    selectedSolutionCards,
    setSelectedSolutionCards,
    submitSolution,
    zoomStep, setZoomStep,
    uiTheme
  };

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  return useContext(GameContext);
};
